/**
 * Mis Cuentas — manage payment methods (bank, SINPE, PayPal, crypto, other)
 * and share them with customers via WhatsApp or PDF.
 *
 * Layout:
 *   - Top bar with "Compartir todas" (WhatsApp dropdown) + "Descargar PDF total"
 *   - List of cards (one per account) with inline edit, delete, share per card
 *   - "Agregar cuenta" opens a dialog with type-aware fields
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { ArrowLeft, Plus, Trash2, Edit, Wallet, MessageCircle, Download, Loader2, Copy, Building2, Smartphone, Globe, Coins, MoreHorizontal } from "lucide-react";
import { api } from "@/App";
import { toast } from "sonner";

const TYPE_ICON = {
  banco: Building2,
  sinpe: Smartphone,
  paypal: Globe,
  cripto: Coins,
  otro: MoreHorizontal,
};

const EMPTY = {
  tipo: "banco",
  banco: "",
  titular: "",
  cedula_titular: "",
  numero_cuenta: "",
  iban: "",
  swift: "",
  moneda: "CRC",
  telefono: "",
  email: "",
  notas: "",
  activo: true,
  orden: 0,
};

export default function BankAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [shareTarget, setShareTarget] = useState(null); // {id, phone, mode}
  const [sharePhone, setSharePhone] = useState("");

  const load = async () => {
    try {
      const [a, t] = await Promise.all([
        api.get("/bank-accounts"),
        api.get("/payment-types"),
      ]);
      setAccounts(a.data);
      setTypes(t.data?.types || []);
    } catch (e) {
      toast.error("No se pudieron cargar las cuentas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (acc) => {
    setEditing(acc);
    setForm({ ...EMPTY, ...acc });
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/bank-accounts/${editing.id}`, form);
        toast.success("Cuenta actualizada");
      } else {
        await api.post("/bank-accounts", form);
        toast.success("Cuenta creada");
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (acc) => {
    if (!confirm(`¿Eliminar la cuenta "${acc.banco || acc.titular || acc.tipo}"?`)) return;
    setBusyId(acc.id);
    try {
      await api.delete(`/bank-accounts/${acc.id}`);
      toast.success("Eliminada");
      await load();
    } catch (e) {
      toast.error("No se pudo eliminar");
    } finally {
      setBusyId("");
    }
  };

  const downloadPdf = async (acc) => {
    setBusyId(acc?.id || "all");
    try {
      const url = acc ? `/bank-accounts/${acc.id}/pdf` : "/bank-accounts/pdf/all";
      const resp = await api.get(url, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const objUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      const cd = resp.headers["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(cd);
      a.download = m?.[1] || (acc ? "cuenta.pdf" : "datos_pago.pdf");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(objUrl);
    } catch (e) {
      toast.error("No se pudo generar el PDF");
    } finally {
      setBusyId("");
    }
  };

  const openShare = (acc) => {
    setShareTarget({ id: acc?.id || "all", label: acc?.banco || acc?.titular || (acc?.tipo === undefined ? "todas las cuentas" : "cuenta") });
    setSharePhone("");
  };

  const sendShare = async () => {
    setBusyId(shareTarget.id);
    try {
      const url = shareTarget.id === "all"
        ? `/bank-accounts/whatsapp-all?phone=${encodeURIComponent(sharePhone)}`
        : `/bank-accounts/${shareTarget.id}/whatsapp?phone=${encodeURIComponent(sharePhone)}`;
      const { data } = await api.get(url);
      window.open(data.wa_url, "_blank");
      setShareTarget(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo compartir");
    } finally {
      setBusyId("");
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copiado al portapapeles");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[40vh]"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="space-y-5 pb-12" data-testid="bank-accounts-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Button variant="ghost" size="sm" asChild className="-ml-2">
            <Link to="/" data-testid="ba-back"><ArrowLeft className="h-4 w-4 mr-1" /> Volver</Link>
          </Button>
          <h1 className="text-3xl sm:text-4xl font-black mt-2" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Mis Cuentas
          </h1>
          <p className="text-sm text-slate-500 mt-1">Centralizá los métodos de pago para enviárselos rápido a tus clientes por WhatsApp o PDF.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {accounts.length > 0 && (
            <>
              <Button variant="outline" onClick={() => openShare(null)} data-testid="ba-share-all" className="rounded-sm">
                <MessageCircle className="h-4 w-4 mr-1.5 text-green-600" /> Compartir todas
              </Button>
              <Button variant="outline" onClick={() => downloadPdf(null)} disabled={busyId === "all"} className="rounded-sm" data-testid="ba-pdf-all">
                {busyId === "all" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />} PDF total
              </Button>
            </>
          )}
          <Button onClick={openCreate} className="bg-[#0B1F3A] hover:bg-[#152C57] text-white rounded-sm" data-testid="ba-add">
            <Plus className="h-4 w-4 mr-1.5" /> Agregar cuenta
          </Button>
        </div>
      </div>

      {accounts.length === 0 ? (
        <Card className="p-10 text-center border-dashed border-slate-300 rounded-sm" data-testid="ba-empty">
          <Wallet className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Todavía no agregaste ninguna cuenta.</p>
          <p className="text-xs text-slate-400 mt-1">Las cuentas se guardan en tu negocio y podés compartirlas con un solo tap.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {accounts.map(acc => {
            const Icon = TYPE_ICON[acc.tipo] || Wallet;
            const typeLabel = types.find(t => t.key === acc.tipo)?.label || acc.tipo;
            return (
              <Card key={acc.id} className={`p-4 border bg-white rounded-sm ${acc.activo === false ? "opacity-60" : ""}`} data-testid={`ba-card-${acc.id}`}>
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-sm bg-blue-50 grid place-items-center shrink-0">
                    <Icon className="h-5 w-5 text-[#0B1F3A]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{typeLabel}</span>
                      {acc.moneda && acc.moneda !== "CRC" && <span className="text-[10px] uppercase tracking-wider font-bold bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded-sm">{acc.moneda}</span>}
                      {acc.activo === false && <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Inactiva</span>}
                    </div>
                    <div className="font-black text-base mt-0.5 truncate" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                      {acc.banco || acc.titular || "Sin nombre"}
                    </div>
                    {acc.titular && acc.banco && <div className="text-xs text-slate-600 truncate">{acc.titular}</div>}
                    <dl className="mt-2 grid grid-cols-1 gap-y-0.5 text-xs">
                      {[
                        ["Cédula", acc.cedula_titular],
                        ["Cuenta", acc.numero_cuenta],
                        ["IBAN", acc.iban],
                        ["SWIFT", acc.swift],
                        ["SINPE", acc.telefono],
                        ["Email", acc.email],
                      ].filter(([_, v]) => v).map(([k, v]) => (
                        <div key={k} className="flex items-center gap-1.5 min-w-0">
                          <dt className="text-slate-500 shrink-0 w-14">{k}:</dt>
                          <dd className="mono text-slate-900 truncate flex-1" title={v}>{v}</dd>
                          <button onClick={() => copy(v)} className="text-slate-400 hover:text-[#0B1F3A] shrink-0" title="Copiar" data-testid={`ba-copy-${acc.id}-${k}`}>
                            <Copy className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </dl>
                    {acc.notas && <p className="text-[11px] text-slate-500 italic mt-2 line-clamp-2">{acc.notas}</p>}
                  </div>
                </div>
                <div className="flex justify-end gap-1 mt-3 pt-3 border-t border-slate-100">
                  <Button variant="ghost" size="sm" onClick={() => openShare(acc)} className="text-green-700 hover:bg-green-50 h-8 rounded-sm" data-testid={`ba-share-${acc.id}`}>
                    <MessageCircle className="h-3.5 w-3.5 mr-1" /> WhatsApp
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => downloadPdf(acc)} disabled={busyId === acc.id} className="h-8 rounded-sm" data-testid={`ba-pdf-${acc.id}`}>
                    {busyId === acc.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1" />} PDF
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(acc)} className="h-8 rounded-sm" data-testid={`ba-edit-${acc.id}`}>
                    <Edit className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(acc)} className="h-8 rounded-sm text-red-600 hover:bg-red-50" data-testid={`ba-delete-${acc.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ba-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              {editing ? "Editar cuenta" : "Nueva cuenta"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm(f => ({ ...f, tipo: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="ba-form-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {types.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>{form.tipo === "paypal" ? "Proveedor" : form.tipo === "cripto" ? "Plataforma / Red" : "Banco"}</Label>
                <Input className="rounded-sm" value={form.banco} onChange={(e) => setForm(f => ({ ...f, banco: e.target.value }))} data-testid="ba-form-banco" placeholder={form.tipo === "banco" ? "BAC, BCR, Nacional…" : ""} />
              </div>
              <div>
                <Label>Moneda</Label>
                <Select value={form.moneda} onValueChange={(v) => setForm(f => ({ ...f, moneda: v }))}>
                  <SelectTrigger className="rounded-sm" data-testid="ba-form-moneda"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CRC">CRC (₡)</SelectItem>
                    <SelectItem value="USD">USD ($)</SelectItem>
                    <SelectItem value="EUR">EUR (€)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Titular</Label>
                <Input className="rounded-sm" value={form.titular} onChange={(e) => setForm(f => ({ ...f, titular: e.target.value }))} data-testid="ba-form-titular" />
              </div>
              <div>
                <Label>Cédula del titular</Label>
                <Input className="rounded-sm" value={form.cedula_titular} onChange={(e) => setForm(f => ({ ...f, cedula_titular: e.target.value }))} data-testid="ba-form-cedula" />
              </div>
            </div>
            {form.tipo === "banco" && (
              <>
                <div>
                  <Label>Número de cuenta</Label>
                  <Input className="rounded-sm mono" value={form.numero_cuenta} onChange={(e) => setForm(f => ({ ...f, numero_cuenta: e.target.value }))} data-testid="ba-form-cuenta" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>IBAN</Label>
                    <Input className="rounded-sm mono" value={form.iban} onChange={(e) => setForm(f => ({ ...f, iban: e.target.value }))} data-testid="ba-form-iban" placeholder="CR05..." />
                  </div>
                  <div>
                    <Label>SWIFT (opcional)</Label>
                    <Input className="rounded-sm mono" value={form.swift} onChange={(e) => setForm(f => ({ ...f, swift: e.target.value }))} data-testid="ba-form-swift" />
                  </div>
                </div>
              </>
            )}
            {form.tipo === "sinpe" && (
              <div>
                <Label>Teléfono SINPE</Label>
                <Input className="rounded-sm" value={form.telefono} onChange={(e) => setForm(f => ({ ...f, telefono: e.target.value }))} data-testid="ba-form-sinpe" placeholder="8XXX-XXXX" />
              </div>
            )}
            {(form.tipo === "paypal" || form.tipo === "otro") && (
              <div>
                <Label>Email / Usuario</Label>
                <Input className="rounded-sm" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} data-testid="ba-form-email" />
              </div>
            )}
            {form.tipo === "cripto" && (
              <div>
                <Label>Dirección / Wallet</Label>
                <Input className="rounded-sm mono" value={form.numero_cuenta} onChange={(e) => setForm(f => ({ ...f, numero_cuenta: e.target.value }))} data-testid="ba-form-wallet" />
              </div>
            )}
            <div>
              <Label>Notas (opcional)</Label>
              <Textarea className="rounded-sm" rows={2} value={form.notas} onChange={(e) => setForm(f => ({ ...f, notas: e.target.value }))} data-testid="ba-form-notas" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.activo !== false} onCheckedChange={(v) => setForm(f => ({ ...f, activo: v }))} data-testid="ba-form-active" />
              <span>Activa (visible al compartir/PDF)</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-[#0B1F3A] hover:bg-[#152C57] text-white rounded-sm" data-testid="ba-form-save">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp phone-picker mini dialog */}
      <Dialog open={!!shareTarget} onOpenChange={(o) => !o && setShareTarget(null)}>
        <DialogContent className="rounded-sm max-w-sm" data-testid="ba-share-dialog">
          <DialogHeader>
            <DialogTitle>Enviar por WhatsApp</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-600">
              {shareTarget?.id === "all"
                ? "Vas a compartir TODAS tus cuentas activas en un solo mensaje."
                : `Vas a compartir: ${shareTarget?.label}`}
            </p>
            <div>
              <Label>Teléfono del cliente (opcional)</Label>
              <Input
                className="rounded-sm"
                value={sharePhone}
                onChange={(e) => setSharePhone(e.target.value)}
                placeholder="8XXX-XXXX  (o dejá vacío para elegir contacto)"
                data-testid="ba-share-phone"
              />
              <p className="text-[11px] text-slate-500 mt-1">Si lo dejás vacío, WhatsApp abre la lista de contactos.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareTarget(null)} className="rounded-sm">Cancelar</Button>
            <Button onClick={sendShare} disabled={!!busyId} className="bg-green-600 hover:bg-green-700 text-white rounded-sm" data-testid="ba-share-confirm">
              <MessageCircle className="h-4 w-4 mr-2" />Abrir WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
